import { useCallback } from 'react';

import {
  Field,
  FieldProps,
  FieldInputProps,
  FieldRenderProps,
} from 'react-final-form';

import { RatingInput as Input, RatingInputItem } from '@vk-hr-tek/ui/input';

import { composeValidators } from '../validation';

/**
 * Компонент ввода рейтинга (RatingInput) с интеграцией в react-final-form.
 * Поддерживает различные типы данных, валидацию, подсказки и другие функции.
 * Специализированный компонент для рейтинговых систем с поддержкой подсветки диапазона.
 *
 * @public
 * @param type - Тип данных, ожидаемых на входе.
 * @param label - Метка для поля ввода.
 * @param name - Уникальное имя поля для react-final-form.
 * @param onChange - Колбэк-функция на изменение значения.
 * @param items - Массив элементов для рейтинга.
 * @param required - Флаг обязательности поля.
 * @param disabled - Флаг отключения поля.
 * @param size - Размер поля.
 * @param showError - Флаг отображения ошибок.
 * @param loading - Флаг загрузки данных.
 * @param tooltip - Текст подсказки.
 * @param testId - Идентификатор для тестирования.
 * @param fillMode - Режим заполнения. Применяет стили flex-лейаута для равномерного распределения чипов.
 * @param highlightRange - Подсветка диапазона. При наведении или выборе подсвечиваются все элементы от начала до текущего значения (для числовых значений).
 * @param showLabelBelow - Отображать label под чипом вместо внутри него.
 * @param validateArray - Массив валидаторов.
 * @param chipMaxWidth - Максимальная ширина отображаемого чипа.
 * @param iconSize - Размер иконки в чипе.
 * @returns Компонент ввода рейтинга.
 */
export const RatingInput = ({
  type,
  label,
  name,
  onChange,
  items,
  required,
  disabled = false,
  size = 'large',
  iconSize,
  showError = true,
  loading = false,
  tooltip,
  testId,
  fillMode = true,
  highlightRange = false,
  showLabelBelow = false,
  validateArray = [],
  chipMaxWidth,
  ...rest
}: {
  type?: 'number' | 'string';
  items: RatingInputItem[];
  fillMode?: boolean;
  highlightRange?: boolean;
  showLabelBelow?: boolean;
  chipMaxWidth?: number;
} & FieldProps<
  string | number | undefined,
  FieldRenderProps<string | number | undefined>
>) => {
  const validators = composeValidators(validateArray);

  const handleChange = useCallback(
    (input: FieldInputProps<string | number | undefined, HTMLElement>) =>
      (nextValue: string | number | undefined) => {
        const convertedVal =
          type === 'number' && typeof nextValue === 'string'
            ? parseInt(nextValue, 10)
            : nextValue;

        input.onChange(convertedVal);

        if (onChange) {
          onChange(convertedVal);
        }
      },
    [type, onChange],
  );

  return (
    <Field name={name} validate={validators} disabled={disabled} {...rest}>
      {({ input, meta }) => {
        return (
          <Input
            {...input}
            testId={testId}
            items={items}
            tooltip={tooltip}
            value={input.value}
            onChange={handleChange(input)}
            size={size}
            iconSize={iconSize}
            required={required}
            error={showError && meta.touched && !!meta.error}
            helperText={showError && meta.touched && meta.error}
            label={label}
            loading={loading}
            disabled={disabled}
            activeSingleOption={false}
            fillMode={fillMode}
            highlightRange={highlightRange}
            showLabelBelow={showLabelBelow}
            chipMaxWidth={chipMaxWidth}
          />
        );
      }}
    </Field>
  );
};
